import { useEffect, useState } from "react";
import { BrandName } from "@/shared/branding";
import { useLocation, useNavigate } from "react-router-dom";
import { QRCodeSVG } from "qrcode.react";
import { Mountain, ShieldCheck, Copy, Loader2, ArrowRight } from "lucide-react";
import { setupMfaEnrollment, verifyMfaEnrollment } from "@/services/auth";
import { ApiError } from "@/services/api";

/**
 * MFAFIX-1 Part 2 · MFA 绑定页
 *
 * 只有一个入口：LoginPage 在收到 `mfa_enrollment_required` 后带着
 * `enrollmentToken` 跳到这里（router state，见 LoginPage.tsx）。没有 token
 * 直接进这个 URL（如手输 / 刷新丢了 state）→ 打回登录页重新走一遍。
 *
 * 样式照 LoginPage（铁律十七·同一套卡片外壳），零新依赖之外只加了 qrcode.react
 * ——本仓此前没有任何真正生成扫码图的能力（drivers/orders 页面的 QrCode 图标都是
 * lucide 占位符，不是真二维码），工单要求"provisioning_uri 渲染成二维码"，
 * 拿来主义：用现成轻量库，不手搓 QR 编码。
 */
export function MfaEnrollPage() {
  const navigate = useNavigate();
  const location = useLocation();
  const enrollmentToken = (location.state as { enrollmentToken?: string } | null)?.enrollmentToken;

  const [secret, setSecret] = useState<string | null>(null);
  const [uri, setUri] = useState<string | null>(null);
  const [code, setCode] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [loadingSetup, setLoadingSetup] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    if (!enrollmentToken) {
      navigate("/login", { replace: true });
      return;
    }
    let cancelled = false;
    (async () => {
      try {
        const res = await setupMfaEnrollment(enrollmentToken);
        if (cancelled) return;
        setSecret(res.secret);
        setUri(res.provisioning_uri);
      } catch (err) {
        if (cancelled) return;
        setError(
          err instanceof ApiError
            ? `初始化失败 · ${err.message}（受限令牌 10 分钟内有效，过期请重新登录）`
            : "网络错误 · 请稍后重试",
        );
      } finally {
        if (!cancelled) setLoadingSetup(false);
      }
    })();
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!enrollmentToken) return;
    setError(null);
    setSubmitting(true);
    try {
      await verifyMfaEnrollment(enrollmentToken, code);
      navigate("/", { replace: true });
    } catch (err) {
      if (err instanceof ApiError) {
        setError(err.status === 401 ? "验证码错误 · 请重新输入" : err.message || "验证失败");
      } else {
        setError("网络错误 · 请稍后重试");
      }
    } finally {
      setSubmitting(false);
    }
  };

  const copySecret = () => {
    if (!secret) return;
    void navigator.clipboard.writeText(secret);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  return (
    <div className="flex min-h-screen items-center justify-center bg-paper p-6">
      <div className="w-full max-w-md rounded-2xl border border-slate-200 bg-white p-8 shadow-sm">
        <div className="mb-6 flex items-center gap-2.5">
          <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-blue-600">
            <Mountain className="h-5 w-5 text-white" strokeWidth={2} />
          </div>
          <div className="flex flex-col">
            <span className="text-lg font-bold text-slate-900"><BrandName /></span>
            <span className="text-[12px] text-slate-500">运营后台 · v1</span>
          </div>
        </div>

        <div className="mb-1 flex items-center gap-2">
          <ShieldCheck className="h-5 w-5 text-blue-600" />
          <h1 className="text-xl font-bold text-slate-900">绑定双重验证（MFA）</h1>
        </div>
        <p className="mb-6 text-[13px] text-slate-500">
          你的角色要求启用 MFA · 首次登录需先完成绑定才能进入系统
        </p>

        {loadingSetup ? (
          <div className="flex items-center justify-center py-10">
            <Loader2 className="h-5 w-5 animate-spin text-slate-400" />
          </div>
        ) : !uri || !secret ? (
          <div className="rounded-lg border border-rose-200 bg-rose-50 px-3 py-2.5 text-[13px] font-medium text-rose-600">
            {error ?? "初始化失败"}
          </div>
        ) : (
          <form onSubmit={submit} className="flex flex-col gap-4">
            <div className="flex flex-col items-center gap-3 rounded-lg border border-slate-200 bg-slate-50 p-4">
              <p className="text-[12px] font-medium text-slate-700">
                用 Google Authenticator / Authy 等验证器 App 扫码
              </p>
              <div className="rounded-lg border border-slate-200 bg-white p-3">
                <QRCodeSVG value={uri} size={168} />
              </div>
              <div className="flex w-full items-center gap-2 rounded-md border border-slate-200 bg-white px-2.5 py-1.5">
                <code className="flex-1 truncate font-mono text-[12px] text-slate-700">{secret}</code>
                <button
                  type="button"
                  onClick={copySecret}
                  title="复制密钥"
                  className="flex items-center gap-1 text-[11px] text-slate-500 hover:text-blue-600"
                >
                  <Copy className="h-3 w-3" /> {copied ? "已复制" : "复制"}
                </button>
              </div>
              <p className="text-[11px] text-slate-400">扫不了码？手动在 App 里输入上面这串密钥</p>
            </div>

            <label className="flex flex-col gap-1">
              <span className="text-[12px] font-medium text-slate-700">验证码（6 位）</span>
              <div className="flex h-10 items-center gap-2 rounded-lg border border-slate-200 bg-white px-3 focus-within:border-blue-500 focus-within:ring-2 focus-within:ring-blue-100">
                <input
                  type="text"
                  inputMode="numeric"
                  pattern="[0-9]{6}"
                  maxLength={6}
                  autoFocus
                  value={code}
                  onChange={(e) => setCode(e.target.value.replace(/\D/g, ""))}
                  placeholder="000000"
                  className="flex-1 bg-transparent text-[13px] outline-none tracking-widest"
                  required
                />
              </div>
            </label>

            {error && <div className="text-[12px] font-medium text-rose-600">{error}</div>}

            <button
              type="submit"
              disabled={submitting || code.length !== 6}
              className="mt-1 flex h-10 items-center justify-center gap-1.5 rounded-lg bg-blue-600 text-[13px] font-semibold text-white hover:bg-blue-700 disabled:opacity-60"
            >
              {submitting ? (
                <Loader2 className="h-4 w-4 animate-spin" />
              ) : (
                <>
                  确认绑定并进入系统 <ArrowRight className="h-3.5 w-3.5" />
                </>
              )}
            </button>
          </form>
        )}
      </div>
    </div>
  );
}
